import {useEffect, useRef, useState} from 'react';

interface UseEventSourceOptions {
  reconnectAttempts?: number;
  onError?: (error: Event) => void;
  onOpen?: (event: Event) => void;
}

const DEFAULT_RECONNECT_ATTEMPTS = 5;
export const useEventSource = (
  onMessage: (event: MessageEvent) => void,
  options?: UseEventSourceOptions
) => {
  const onMessageCBRef = useRef(onMessage);
  const attempts = useRef<number>(0);
  const urlForConnect = useRef<string>('');
  const [state, setState] = useState({
    isConnected: false,
    reconnectTimer: null as NodeJS.Timeout | null,
  });
  const isConnectionReqCalled = useRef<boolean>(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  const connect = (url: string) => {
    if (urlForConnect.current !== url) urlForConnect.current = url;
    if (
      isConnectionReqCalled.current ||
      state.isConnected ||
      eventSourceRef.current ||
      attempts.current >=
        (options?.reconnectAttempts ?? DEFAULT_RECONNECT_ATTEMPTS)
    ) {
      return;
    }
    isConnectionReqCalled.current = true;

    const eventSource = new EventSource(url);

    eventSource.onopen = (event) => {
      attempts.current = 0;
      setState((prev) => ({
        ...prev,
        isConnected: true,
      }));
      isConnectionReqCalled.current = false;
      options?.onOpen?.(event);
    };

    eventSource.onmessage = (event) => {
      onMessageCBRef.current(event);
    };

    eventSource.onerror = (error) => {
      options?.onError?.(error);
      eventSourceRef.current = null;
      setState((prev) => ({
        ...prev,
        isConnected: false,
      }));
      isConnectionReqCalled.current = false;
      eventSource.close();
      if (attempts.current <= (options?.reconnectAttempts ?? 5)) {
        if (state.reconnectTimer) clearTimeout(state.reconnectTimer);
        const timer = setTimeout(() => {
          connect(urlForConnect.current);
        }, 2000);
        attempts.current += 1;
        setState((prev) => ({
          ...prev,
          reconnectTimer: timer,
        }));
      }
    };
    eventSourceRef.current = eventSource;
    setState((prev) => ({
      ...prev,
    }));
  };

  const close = () => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    if (state.reconnectTimer) clearTimeout(state.reconnectTimer);
    attempts.current = 0;
    urlForConnect.current = '';
    setState({
      isConnected: false,
      reconnectTimer: null,
    });
  };

  useEffect(() => {
    return () => close();
  }, []);

  useEffect(() => {
    onMessageCBRef.current = onMessage;
  }, [onMessage]);

  return {isConnected: state.isConnected, connect, close};
};
